/**
 * Colour helpers.
 *
 * Colours are stored as `#rrggbb` in the document and the store, and as RGBA
 * bytes when touching pixel buffers. Keeping the two conversions here means the
 * eyedropper, the flood fill and the brush all agree on what a colour is.
 */

export interface Rgba {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

export const clampByte = (value: number): number =>
  Math.max(0, Math.min(255, Math.round(Number.isFinite(value) ? value : 0)));

export function hexToRgba(hex: string, alpha = 1): Rgba {
  // 'transparent' is a legal Colour in the document model (types.ts), so it must
  // not fall through the hex parser and come out as opaque black.
  if (hex.trim().toLowerCase() === 'transparent') {
    return { red: 0, green: 0, blue: 0, alpha: 0 };
  }
  const clean = hex.trim().replace(/^#/, '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean.padEnd(6, '0').slice(0, 6);
  const int = Number.parseInt(full, 16);
  return {
    red: (int >> 16) & 255,
    green: (int >> 8) & 255,
    blue: int & 255,
    alpha: clampByte(alpha * 255),
  };
}

export function rgbaToHex(color: Rgba): string {
  const hex = (color.red << 16) | (color.green << 8) | color.blue;
  return `#${hex.toString(16).padStart(6, '0')}`;
}

/** Squared distance in RGB space — the metric the flood fill's tolerance uses. */
export function colourDistanceSquared(a: Rgba, b: Rgba): number {
  const dr = a.red - b.red;
  const dg = a.green - b.green;
  const db = a.blue - b.blue;
  const da = a.alpha - b.alpha;
  return dr * dr + dg * dg + db * db + da * da;
}

/** Tolerance as entered (0..100) mapped to squared RGB distance. */
export function toleranceToSquared(tolerance: number): number {
  const channel = (Math.max(0, Math.min(100, tolerance)) / 100) * 255;
  return channel * channel * 4;
}
